import clsx from 'clsx'
import { Check } from 'lucide-react'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'

import { AwxConnectedAccountType } from '@ankor-io/common/types/awx'
import { Button } from '@ankor-io/ui-components/src/Button'
import { Dialog, DialogActions, DialogBody, DialogTitle } from '@ankor-io/ui-components/src/Dialog'
import { Loading } from '@ankor-io/ui-components/src/Loading'
import { Text } from '@ankor-io/ui-components/src/Text'

import { useActivateModuleMutation } from '@/api/queries/useVesselQuery'
import { useModuleTheme } from '@/providers/moduleTheme'
import { testAwareMs } from '@/utils/testAwareMs'

interface ActivateModuleModalProps {
  isOpen: boolean
  onClose: () => void
  moduleType: AwxConnectedAccountType
}

interface ModuleContent {
  title: string
  label: string
  features: string[]
  icon: string
}

const MODULE_CONTENT: Record<AwxConnectedAccountType, ModuleContent> = {
  [AwxConnectedAccountType.APA]: {
    title: 'Activate APA Management',
    label: 'APA',
    icon: '💳',
    features: ['Multi-currency accounts', 'Expense management', 'Complete Charters', 'Issue virtual or physical cards'],
  },
  [AwxConnectedAccountType.OPERATIONS]: {
    title: 'Activate Operations Management',
    label: 'OPS',
    icon: '⚙️',
    features: [
      'Multi-currency accounts',
      'Operations expense management',
      'Transfer funds to APA',
      'Issue virtual or physical cards',
    ],
  },
}

interface LoadingStep {
  label: string
  status: 'pending' | 'loading' | 'complete'
}

const LOADING_STEPS: LoadingStep[] = [
  { label: 'Module initialization', status: 'pending' },
  { label: 'Creating your Wallet', status: 'pending' },
  { label: 'Enabling physical and virtual cards', status: 'pending' },
  { label: 'Finishing up', status: 'pending' },
]

const STEP_INDICATOR_STYLES: Record<LoadingStep['status'], string> = {
  pending: 'border-2 border-gray-300 bg-gray-50 dark:border-gray-600 dark:bg-gray-800',
  loading: 'bg-blue-50 dark:bg-blue-900/20',
  complete: 'border-2 border-green-500 bg-green-50 dark:bg-green-900/20',
}

export const ActivateModuleModal = ({ isOpen, onClose, moduleType }: ActivateModuleModalProps) => {
  const { moduleTheme } = useModuleTheme()
  const activateModuleMutation = useActivateModuleMutation()

  const [isActivating, setIsActivating] = useState(false)
  const [loadingSteps, setLoadingSteps] = useState<LoadingStep[]>(LOADING_STEPS)

  const content = MODULE_CONTENT[moduleType]
  const DELAY_TIME = testAwareMs(500)

  // Reset steps when modal closes; clear any in-flight animation timeouts on unmount
  useEffect(() => {
    if (!isOpen) {
      setIsActivating(false)
      setLoadingSteps(LOADING_STEPS)
    }
  }, [isOpen])

  const updateStepStatus = (index: number, status: LoadingStep['status']) => {
    setLoadingSteps((prev) => prev.map((step, i) => (i === index ? { ...step, status } : step)))
  }

  const animateLoadingSteps = async () => {
    for (let i = 0; i < LOADING_STEPS.length; i++) {
      updateStepStatus(i, 'loading')
      await new Promise((resolve) => setTimeout(resolve, DELAY_TIME))
      updateStepStatus(i, 'complete')
    }
  }

  const handleActivate = async () => {
    setIsActivating(true)

    try {
      await Promise.all([animateLoadingSteps(), activateModuleMutation.mutateAsync()])

      await new Promise((resolve) => setTimeout(resolve, DELAY_TIME))

      toast.success(`${content.label} module activated successfully!`)
      onClose()
    } catch (error) {
      console.error('Error activating module:', error)

      const errorMessage = error instanceof Error ? error.message : String(error)
      if (errorMessage.includes('already') || errorMessage.includes('activated')) {
        toast.error(`${content.label} module is already activated`)
      } else {
        toast.error(`Failed to activate ${content.label} module`)
      }

      setIsActivating(false)
    }
  }

  return (
    <Dialog
      open={isOpen}
      onClose={isActivating ? () => {} : onClose}
      size="lg"
    >
      {!isActivating ? (
        <>
          <DialogTitle>{content.title}</DialogTitle>
          <DialogBody>
            <Text className="mb-4">You have access to this module. Activate it to start using:</Text>
            <div className="space-y-3">
              {content.features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-center gap-x-3"
                >
                  <div className="flex size-6 items-center justify-center rounded-full border-2 border-gray-300 bg-gray-50 dark:border-gray-600 dark:bg-gray-800">
                    <Check className="size-4 text-gray-400 dark:text-gray-500" />
                  </div>
                  <Text>{feature}</Text>
                </div>
              ))}
            </div>
          </DialogBody>
          <DialogActions>
            <Button
              plain
              onClick={onClose}
            >
              Maybe Later
            </Button>
            <Button
              color={moduleTheme.button}
              onClick={handleActivate}
            >
              Activate Now
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>
            <div className="flex items-center gap-x-2">
              <div className="flex size-12 items-center justify-center rounded-xl bg-purple-100 dark:bg-purple-900/20">
                <Loading size="md" />
              </div>
              <span>Activating {content.label} Management</span>
            </div>
          </DialogTitle>
          <DialogBody>
            <Text className="mb-4 text-gray-600 dark:text-gray-300">Finishing up...</Text>
            <div className="space-y-3">
              {loadingSteps.map((step, index) => (
                <div
                  key={index}
                  className="flex items-center gap-x-3"
                >
                  <div
                    className={clsx(
                      'flex size-6 items-center justify-center rounded-full',
                      STEP_INDICATOR_STYLES[step.status],
                    )}
                  >
                    {step.status === 'complete' && <Check className="size-4 text-green-600 dark:text-green-400" />}
                    {step.status === 'loading' && <Loading />}
                  </div>
                  <Text
                    className={
                      step.status === 'pending'
                        ? 'text-gray-400 dark:text-gray-500'
                        : 'text-gray-900 dark:text-gray-100'
                    }
                  >
                    {step.label}
                  </Text>
                </div>
              ))}
            </div>
          </DialogBody>
        </>
      )}
    </Dialog>
  )
}
